import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateMutation, useGetAdminCountQuery } from '../../app/services/users';
import { fetchUsers } from '../../features/users/usersSlice';
import ModalComponent from '../ModalComponent';
import { useTranslation } from 'react-i18next';
import { iconButtonStyles } from '../../utils/iconButtonStyles';

import {
  Typography,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { ModalButton, ModalDialogActions } from '../ModalComponents';

import styles from '../ModalComponent/modalStyles.module.css';
import { selectUserById } from '../../features/users/usersSlice';

export default function ModalStatusUser({ id, action }) {
  const [open, setOpen] = useState(false);
  const [errorResponse, setErrorResponse] = useState('');

  const selectedUser = useSelector((state) => selectUserById(state, id));

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [updateUser] = useUpdateMutation();

  const isArchiveAction = action === 'archived';
  const { data: adminCountData } = useGetAdminCountQuery(undefined, {
    skip: !isArchiveAction || selectedUser?.role !== 'admin',
  });
  const isLastAdmin =
    isArchiveAction &&
    selectedUser?.role === 'admin' &&
    selectedUser?.status === 'active' &&
    adminCountData?.activeAdmins === 1;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!selectedUser) return;
    try {
      const { id, ...userData } = selectedUser;
      userData.status = action;
      await updateUser({ id, userData }).unwrap();
      dispatch(fetchUsers());
      setOpen(false);
    } catch (error) {
      const key = error?.data?.message;
      const known = key && t(`errors.${key}`, { defaultValue: '' });
      setErrorResponse(known || key || t('errors.generic'));
      console.error('Error updating user:', error);
    }
  };

  return (
    <div>
      <Tooltip
        title={
          isLastAdmin
            ? t('errors.cannotRemoveLastAdmin')
            : action === 'archived'
            ? t('modals.archive')
            : t('modals.reactivate')
        }
        placement="top"
      >
        <span>
          <IconButton
            color="primary"
            onClick={handleOpen}
            style={iconButtonStyles}
            disabled={isLastAdmin}
          >
            {action === 'archived' ? <ArchiveIcon /> : <UnarchiveIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={{ m: 0, p: 2 }}>
            {t('modals.areSureWant')}{' '}
            {action === 'archived' ? t('modals.archive') : t('modals.reactivate')}{' '}
            {t('modals.thisUser')}?
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers sx={{ pr: 2, pl: 2 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              {selectedUser && (
                <Typography
                  variant="h6"
                  style={{ color: 'var(--text-heading)', fontWeight: 'bold' }}
                >
                  {`${selectedUser?.firstname} ${selectedUser?.lastname}`}
                </Typography>
              )}
            </div>
            <Typography color="var(--text-secondary)" variant="body2" pt={1}>
              {action === 'archived'
                ? t('generic.archiveUserDescription')
                : t('generic.restoreUserDescription')}
            </Typography>
          </DialogContent>

          <ModalDialogActions>
            <ModalButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isLastAdmin}
            >
              {action === 'archived' ? t('modals.archive') : t('modals.reactivate')}
            </ModalButton>
          </ModalDialogActions>

          {errorResponse && (
            <DialogContent sx={{ p: 3, pt: 0 }}>
              {Array.isArray(errorResponse) ? (
                errorResponse.map((item, index) => (
                  <Typography key={index} color="error">
                    {item}
                  </Typography>
                ))
              ) : (
                <Typography color="error">{errorResponse}</Typography>
              )}
            </DialogContent>
          )}
        </div>
      </ModalComponent>
    </div>
  );
}
